import React from 'react';
import {
    Box, Container, GridLegacy as Grid, Card, CardContent, Typography, Button, Avatar, Chip, IconButton, List, ListItem,
    ListItemText, ListItemIcon, Divider, Badge, Paper, styled, alpha, useTheme, useMediaQuery
} from '@mui/material';
import {
    Star, AutoAwesome, Chat, Favorite, TrendingUp, Psychology, CalendarToday, NotificationsActive,
    Settings, Upgrade, PlayArrow, Share, BookmarkBorder, LightMode
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

// Types
interface UserProfile {
    name: string;
    avatar: string;
    plan: 'free' | 'pro' | 'elite';
    joinDate: string;
    birthSign: string;
    moonSign: string;
    risingSign: string;
}

interface DailyReading {
    title: string;
    content: string;
    mood: string;
    luckyNumber: number;
    luckyColor: string;
    compatibility: string;
}

interface RecentChat {
    id: string;
    question: string;
    timestamp: string;
    preview: string;
}

interface UpcomingEvent {
    id: string;
    title: string;
    date: string;
    type: 'transit' | 'moon_phase' | 'personal';
    impact: 'high' | 'medium' | 'low';
}

// Styled Components
const DashboardCard = styled(Card)(({ theme }) => ({
    background: `linear-gradient(135deg, 
    ${alpha(theme.palette.background.paper, 0.8)} 0%, 
    ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
    backdropFilter: 'blur(20px)',
    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
    borderRadius: '24px',
    transition: 'all 0.3s ease',
    '&:hover': {
        borderColor: alpha(theme.palette.primary.main, 0.3),
        transform: 'translateY(-4px)',
        boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.2)}`,
    },
}));

const PremiumCard = styled(Card)(({ theme }) => ({
    background: `linear-gradient(135deg, 
    ${alpha(theme.palette.primary.main, 0.2)} 0%, 
    ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
    backdropFilter: 'blur(20px)',
    border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
    borderRadius: '24px',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '4px',
        background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    }
}));

const StatCard = styled(Paper)(({ theme }) => ({
    background: `linear-gradient(135deg, 
    ${alpha(theme.palette.background.paper, 0.6)} 0%, 
    ${alpha(theme.palette.info.main, 0.1)} 100%)`,
    backdropFilter: 'blur(10px)',
    border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
    borderRadius: '16px',
    padding: theme.spacing(3),
    textAlign: 'center',
    transition: 'all 0.3s ease',
    '&:hover': {
        transform: 'scale(1.02)',
    }
}));

const AIDashboard: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { t } = useTranslation();
    console.log(isMobile);

    // Mock data - in real app this would come from API
    const userProfile: UserProfile = {
        name: "Sarah Chen",
        avatar: "/api/placeholder/80/80",
        plan: "pro",
        joinDate: "2024-01-15",
        birthSign: "Gemini",
        moonSign: "Scorpio",
        risingSign: "Leo"
    };

    const dailyReading: DailyReading = {
        title: t('dashboard.cosmicForecast'),
        content: t('dashboard.dailyReadingContent'),
        mood: t('common.transformative'),
        luckyNumber: 7,
        luckyColor: t('common.deepPurple'),
        compatibility: t('common.piscesCancer')
    };

    const recentChats: RecentChat[] = [
        {
            id: "1",
            question: "What does my birth chart say about career?",
            timestamp: "2 hours ago",
            preview: "Your Midheaven in Capricorn suggests..."
        },
        {
            id: "2",
            question: "Love compatibility with a Virgo?",
            timestamp: "Yesterday",
            preview: "Gemini and Virgo can create a beautiful..."
        },
        {
            id: "3",
            question: "Best time for major decisions?",
            timestamp: "3 days ago",
            preview: "With your current transits..."
        }
    ];

    const upcomingEvents: UpcomingEvent[] = [
        {
            id: "1",
            title: "New Moon in Aquarius",
            date: "Feb 9",
            type: "moon_phase",
            impact: "high"
        },
        {
            id: "2",
            title: "Venus enters your 7th house",
            date: "Feb 14",
            type: "personal",
            impact: "medium"
        },
        {
            id: "3",
            title: "Mercury Retrograde begins",
            date: "Mar 1",
            type: "transit",
            impact: "high"
        }
    ];

    const getPlanColor = (plan: string) => {
        switch (plan) {
            case 'elite': return theme.palette.warning.main;
            case 'pro': return theme.palette.primary.main;
            default: return theme.palette.text.secondary;
        }
    };

    const getPlanLabel = (plan: string) => {
        switch (plan) {
            case 'elite': return t('common.elite');
            case 'pro': return t('common.pro');
            default: return t('common.free');
        }
    };

    const getImpactColor = (impact: string) => {
        switch (impact) {
            case 'high': return theme.palette.error.main;
            case 'medium': return theme.palette.warning.main;
            default: return theme.palette.success.main;
        }
    };

    const getImpactLabel = (impact: string) => {
        switch (impact) {
            case 'high': return t('common.high');
            case 'medium': return t('common.medium');
            default: return t('common.low');
        }
    };

    return (
        <Box sx={{
            minHeight: '100vh',
            background: `linear-gradient(135deg, 
        #030712 0%, 
        ${alpha(theme.palette.primary.dark, 0.1)} 25%,
        ${alpha(theme.palette.secondary.dark, 0.05)} 75%,
        #030712 100%)`,
            pt: 4,
            pb: 8
        }}>
            <Container maxWidth="xl">
                {/* Header Section */}
                <Box sx={{ mb: 4 }}>
                    <Grid container spacing={3} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                <Avatar
                                    src={userProfile.avatar}
                                    sx={{
                                        width: 80,
                                        height: 80,
                                        border: `3px solid ${theme.palette.primary.main}`,
                                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
                                    }}
                                >
                                    {userProfile.name.charAt(0)}
                                </Avatar>
                                <Box>
                                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                                        {t('dashboard.welcome', { name: userProfile.name })}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                        <Chip
                                            label={getPlanLabel(userProfile.plan)}
                                            sx={{
                                                background: `linear-gradient(135deg, ${getPlanColor(userProfile.plan)}, ${alpha(getPlanColor(userProfile.plan), 0.7)})`,
                                                color: 'white',
                                                fontWeight: 600
                                            }}
                                        />
                                        <Typography variant="body2" color="text.secondary">
                                            {userProfile.birthSign} ☉ {userProfile.moonSign} ☽ {userProfile.risingSign} ↗
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Box sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' }, gap: 2 }}>
                                <IconButton sx={{ color: 'text.secondary' }}>
                                    <NotificationsActive />
                                </IconButton>
                                <IconButton sx={{ color: 'text.secondary' }}>
                                    <Settings />
                                </IconButton>
                                {userProfile.plan === 'free' && (
                                    <Button
                                        startIcon={<Upgrade />}
                                        sx={{
                                            background: `linear-gradient(135deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`,
                                            color: 'common.black',
                                            fontWeight: 600,
                                            px: 3,
                                            borderRadius: 3,
                                            textTransform: 'none'
                                        }}
                                    >
                                        {t('dashboard.upgrade')}
                                    </Button>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </Box>

                {/* Stats Row */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={6} md={3}>
                        <StatCard>
                            <AutoAwesome sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                                127
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('dashboard.aiReadings')}
                            </Typography>
                        </StatCard>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <StatCard>
                            <Chat sx={{ fontSize: 32, color: 'secondary.main', mb: 1 }} />
                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                                43
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('dashboard.chatSessions')}
                            </Typography>
                        </StatCard>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <StatCard>
                            <Star sx={{ fontSize: 32, color: 'warning.main', mb: 1 }} />
                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                                89%
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('dashboard.accuracyScore')}
                            </Typography>
                        </StatCard>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <StatCard>
                            <TrendingUp sx={{ fontSize: 32, color: 'success.main', mb: 1 }} />
                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                                +12
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('dashboard.daysStreak')}
                            </Typography>
                        </StatCard>
                    </Grid>
                </Grid>

                {/* Main Content */}
                <Grid container spacing={4}>
                    {/* Left Column */}
                    <Grid item xs={12} lg={8}>
                        {/* Daily Reading */}
                        <DashboardCard sx={{ mb: 4 }}>
                            <CardContent sx={{ p: 4 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <LightMode sx={{ color: 'warning.main', fontSize: 28 }} />
                                        <Typography variant="h5" sx={{ fontWeight: 600 }}>
                                            {dailyReading.title}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <IconButton size="small" sx={{ color: 'text.secondary' }}>
                                            <Share />
                                        </IconButton>
                                        <IconButton size="small" sx={{ color: 'text.secondary' }}>
                                            <BookmarkBorder />
                                        </IconButton>
                                    </Box>
                                </Box>

                                <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.7, color: 'text.secondary' }}>
                                    {dailyReading.content}
                                </Typography>

                                <Grid container spacing={3}>
                                    <Grid item xs={6} md={3}>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                {t('dashboard.todaysMood')}
                                            </Typography>
                                            <Chip
                                                label={dailyReading.mood}
                                                sx={{
                                                    background: alpha(theme.palette.info.main, 0.2),
                                                    color: 'info.main',
                                                    fontWeight: 600
                                                }}
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                {t('dashboard.luckyNumber')}
                                            </Typography>
                                            <Typography variant="h4" sx={{ fontWeight: 700, color: 'warning.main' }}>
                                                {dailyReading.luckyNumber}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                {t('dashboard.luckyColor')}
                                            </Typography>
                                            <Box sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: 1
                                            }}>
                                                <Box sx={{
                                                    width: 20,
                                                    height: 20,
                                                    borderRadius: '50%',
                                                    background: 'linear-gradient(135deg, #6366F1, #8B5CF6)',
                                                    border: '2px solid white'
                                                }} />
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    {dailyReading.luckyColor}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                {t('dashboard.bestMatch')}
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {dailyReading.compatibility}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </DashboardCard>

                        {/* AI Chat Quick Access */}
                        <PremiumCard sx={{ mb: 4 }}>
                            <CardContent sx={{ p: 4 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Psychology sx={{ color: 'primary.main', fontSize: 28 }} />
                                        <Typography variant="h5" sx={{ fontWeight: 600 }}>
                                            {t('dashboard.aiAstrologerChat')}
                                        </Typography>
                                        <Badge badgeContent={t('common.pro')} color="primary" sx={{ ml: 1 }} />
                                    </Box>
                                </Box>

                                <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                                    {t('dashboard.aiAstrologerDescription')}
                                </Typography>

                                <Button
                                    fullWidth
                                    size="large"
                                    startIcon={<Chat />}
                                    sx={{
                                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                        color: 'white',
                                        py: 2,
                                        borderRadius: 3,
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        fontSize: '1.1rem',
                                        '&:hover': {
                                            background: `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.secondary.light})`,
                                        }
                                    }}
                                >
                                    {t('dashboard.startNewConversation')}
                                </Button>
                            </CardContent>
                        </PremiumCard>

                        {/* Recent Chats */}
                        <DashboardCard>
                            <CardContent sx={{ p: 4 }}>
                                <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
                                    {t('dashboard.recentConversations')}
                                </Typography>
                                <List sx={{ p: 0 }}>
                                    {recentChats.map((chat, index) => (
                                        <React.Fragment key={chat.id}>
                                            <ListItem
                                                sx={{
                                                    px: 0,
                                                    py: 2,
                                                    cursor: 'pointer',
                                                    borderRadius: 2,
                                                    '&:hover': {
                                                        background: alpha(theme.palette.primary.main, 0.05)
                                                    }
                                                }}
                                            >
                                                <ListItemIcon>
                                                    <Chat sx={{ color: 'primary.main' }} />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={chat.question}
                                                    secondary={
                                                        <Box>
                                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                                {chat.preview}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {chat.timestamp}
                                                            </Typography>
                                                        </Box>
                                                    }
                                                    primaryTypographyProps={{ fontWeight: 600 }}
                                                />
                                                <IconButton size="small">
                                                    <PlayArrow />
                                                </IconButton>
                                            </ListItem>
                                            {index < recentChats.length - 1 && <Divider />}
                                        </React.Fragment>
                                    ))}
                                </List>
                            </CardContent>
                        </DashboardCard>
                    </Grid>

                    {/* Right Column */}
                    <Grid item xs={12} lg={4}>
                        {/* Upcoming Cosmic Events */}
                        <DashboardCard sx={{ mb: 4 }}>
                            <CardContent sx={{ p: 4 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                                    <CalendarToday sx={{ color: 'info.main', fontSize: 24 }} />
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        {t('dashboard.cosmicCalendar')}
                                    </Typography>
                                </Box>

                                <List sx={{ p: 0 }}>
                                    {upcomingEvents.map((event, index) => (
                                        <React.Fragment key={event.id}>
                                            <ListItem sx={{ px: 0, py: 2 }}>
                                                <Box sx={{ mr: 2, textAlign: 'center', minWidth: 50 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 700, color: getImpactColor(event.impact) }}>
                                                        {event.date}
                                                    </Typography>
                                                </Box>
                                                <ListItemText
                                                    primary={event.title}
                                                    secondary={
                                                        <Chip
                                                            label={getImpactLabel(event.impact)}
                                                            size="small"
                                                            sx={{
                                                                mt: 0.5,
                                                                background: alpha(getImpactColor(event.impact), 0.2),
                                                                color: getImpactColor(event.impact),
                                                                fontSize: '0.7rem',
                                                                height: 20
                                                            }}
                                                        />
                                                    }
                                                    primaryTypographyProps={{
                                                        fontWeight: 600,
                                                        fontSize: '0.9rem'
                                                    }}
                                                />
                                            </ListItem>
                                            {index < upcomingEvents.length - 1 && <Divider />}
                                        </React.Fragment>
                                    ))}
                                </List>
                            </CardContent>
                        </DashboardCard>

                        {/* Birth Chart Widget */}
                        <DashboardCard sx={{ mb: 4 }}>
                            <CardContent sx={{ p: 4, textAlign: 'center' }}>
                                <Star sx={{ color: 'warning.main', fontSize: 40, mb: 2 }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                    {t('dashboard.yourBirthChart')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                    {t('dashboard.birthChartDescription')}
                                </Typography>
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    sx={{
                                        borderColor: 'primary.main',
                                        color: 'primary.main',
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        borderRadius: 2,
                                        '&:hover': {
                                            borderColor: 'primary.light',
                                            background: alpha(theme.palette.primary.main, 0.1)
                                        }
                                    }}
                                >
                                    {t('dashboard.viewFullChart')}
                                </Button>
                            </CardContent>
                        </DashboardCard>

                        {/* Compatibility Widget */}
                        <DashboardCard>
                            <CardContent sx={{ p: 4, textAlign: 'center' }}>
                                <Favorite sx={{ color: 'secondary.main', fontSize: 40, mb: 2 }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                    {t('dashboard.loveCompatibility')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                    {t('dashboard.compatibilityDescription')}
                                </Typography>
                                {userProfile.plan === 'free' ? (
                                    <Button
                                        fullWidth
                                        sx={{
                                            background: `linear-gradient(135deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`,
                                            color: 'common.black',
                                            fontWeight: 600,
                                            textTransform: 'none',
                                            borderRadius: 2
                                        }}
                                    >
                                        {t('dashboard.upgradeForAccess')}
                                    </Button>
                                ) : (
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        sx={{
                                            borderColor: 'secondary.main',
                                            color: 'secondary.main',
                                            fontWeight: 600,
                                            textTransform: 'none',
                                            borderRadius: 2,
                                            '&:hover': {
                                                borderColor: 'secondary.light',
                                                background: alpha(theme.palette.secondary.main, 0.1)
                                            }
                                        }}
                                    >
                                        {t('dashboard.checkCompatibility')}
                                    </Button>
                                )}
                            </CardContent>
                        </DashboardCard>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};

export default AIDashboard;